import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from '@/utils/axios'
import { isAdminClient } from '@/utils/userEnvironment'
import { formatStorage } from '@wox/dayjs'
import useCurrentLocationId from './useCurrentLocationId'

// Types matching backend DTOs
export interface TSlotWeekdays {
  sun: boolean
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
}

export interface TSlotDateOverride {
  date: string // YYYY-MM-DD format
  start_time: string // HH:mm:ss format
  end_time: string // HH:mm:ss format
}

export interface SimpleResource {
  id: number
  name: string
}

export interface BookingSlotTemplate {
  id: number
  space_id: number
  location_id: number
  name: string
  start_time: string
  end_time: string
  spans_midnight: boolean
  weekdays: TSlotWeekdays
  capacity: number
  description?: string
  position: number
  is_enabled: boolean
  date_overrides: TSlotDateOverride[]
  resources?: SimpleResource[]
  created_at: string
  updated_at: string
  deleted_at?: string
}

export type SlotUnavailableReason = 'advance_limitation' | 'no_permission'

export interface EffectiveSlot {
  template_id: number
  name: string
  start_at: string
  end_at: string
  is_available: boolean
  capacity: number
  booked_count: number
  remaining_capacity: number
  description?: string
  unavailable_reason?: SlotUnavailableReason
  is_carryover?: boolean
}

export interface CreateBookingSlotTemplateDto {
  location_id: number
  resource_ids?: number[]
  name: string
  start_time: string
  end_time: string
  weekdays: TSlotWeekdays
  capacity?: number
  description?: string
  position?: number
  is_enabled?: boolean
  date_overrides?: TSlotDateOverride[]
}

export interface UpdateBookingSlotTemplateDto
  extends Partial<CreateBookingSlotTemplateDto> {}

export interface QueryBookingSlotTemplateDto {
  location_id?: number
  resource_id?: number
  is_enabled?: boolean
  search?: string
}

// Query keys
export const bookingSlotKeys = {
  all: ['booking-slot-templates'] as const,
  lists: () => [...bookingSlotKeys.all, 'list'] as const,
  list: (params: QueryBookingSlotTemplateDto) =>
    [...bookingSlotKeys.lists(), params] as const,
  details: () => [...bookingSlotKeys.all, 'detail'] as const,
  detail: (id: number) => [...bookingSlotKeys.details(), id] as const,
  availableSlots: (resourceId: number, dates: string[]) =>
    ['available-slots', resourceId, dates] as const,
}

export function useInvalidateBookingSlotTemplates() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: bookingSlotKeys.lists() })
  }
}

// Admin Hooks

/**
 * List booking slot templates for admin
 */
export function useBookingSlotTemplateIndex(
  params: QueryBookingSlotTemplateDto
) {
  return useQuery({
    queryKey: bookingSlotKeys.list(params),
    queryFn: async () => {
      const res = await client<{ data: BookingSlotTemplate[] }>({
        method: 'get',
        url: '/api/v5/admin/booking-slot-templates',
        params,
      })
      return res.data
    },
    enabled: !!params.location_id,
  })
}

/**
 * Get a single booking slot template
 */
export function useBookingSlotTemplateShow(id: number | undefined) {
  return useQuery({
    queryKey: bookingSlotKeys.detail(id ?? 0),
    queryFn: async () => {
      const res = await client<{ data: BookingSlotTemplate }>({
        method: 'get',
        url: `/api/v5/admin/booking-slot-templates/${id}`,
      })
      return res.data
    },
    enabled: !!id,
  })
}

/**
 * Create a booking slot template
 */
export function useBookingSlotTemplateCreate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateBookingSlotTemplateDto) => {
      const res = await client<{ data: BookingSlotTemplate }>({
        method: 'post',
        url: '/api/v5/admin/booking-slot-templates',
        data,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingSlotKeys.lists() })
    },
  })
}

/**
 * Update a booking slot template
 */
export function useBookingSlotTemplateUpdate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: UpdateBookingSlotTemplateDto
    }) => {
      const res = await client<{ data: BookingSlotTemplate }>({
        method: 'put',
        url: `/api/v5/admin/booking-slot-templates/${id}`,
        data,
      })
      return res.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: bookingSlotKeys.lists() })
      queryClient.invalidateQueries({ queryKey: bookingSlotKeys.detail(id) })
    },
  })
}

/**
 * Delete a booking slot template
 */
export function useBookingSlotTemplateDelete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await client({
        method: 'delete',
        url: `/api/v5/admin/booking-slot-templates/${id}`,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingSlotKeys.lists() })
    },
  })
}

// User Hooks

/**
 * Get slot templates linked to a resource (user-facing API)
 */
export function useResourceSlotTemplates(
  resourceId: number | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: ['resource-slot-templates', resourceId],
    queryFn: async () => {
      const res = await client<{ data: BookingSlotTemplate[] }>({
        method: 'get',
        url: `/api/v5/resources/${resourceId}/slot-templates`,
      })
      return res.data
    },
    enabled: enabled && !!resourceId,
  })
}

/**
 * Get available slots for multiple resources on a specific date.
 * Used by the calendar view to display slot zones for all visible resources.
 * @param isAdmin - When true, uses admin API path so booking rules are skipped.
 */
export function useMultiResourceAvailableSlots(
  resourceIds: number[],
  date: string, // YYYY-MM-DD
  timezone: string,
  enabled: boolean,
  isAdmin = false
) {
  return useQuery({
    queryKey: [
      'multi-resource-available-slots',
      resourceIds,
      date,
      timezone,
      isAdmin,
    ],
    queryFn: async () => {
      const results: Record<number, EffectiveSlot[]> = {}
      await Promise.all(
        resourceIds.map(async (resourceId) => {
          try {
            const url = isAdmin
              ? `/api/v5/admin/booking-slot-templates/resources/${resourceId}/available-slots`
              : `/api/v5/resources/${resourceId}/available-slots`
            const res = await client<{
              data: Record<string, EffectiveSlot[]>
            }>({
              method: 'get',
              url,
              params: { dates: [date], timezone },
            })
            results[resourceId] = res.data?.[date] || []
          } catch {
            results[resourceId] = []
          }
        })
      )
      return results
    },
    enabled: enabled && resourceIds.length > 0,
    staleTime: 30000,
  })
}

/**
 * Get available slots for a resource on specific dates
 */
export function useResourceAvailableSlots(
  resourceId: number | undefined,
  dates: string[],
  timezone?: string
) {
  const isAdmin = isAdminClient()
  return useQuery({
    queryKey: [
      ...bookingSlotKeys.availableSlots(resourceId ?? 0, dates),
      isAdmin,
    ],
    queryFn: async () => {
      const url = isAdmin
        ? `/api/v5/admin/booking-slot-templates/resources/${resourceId}/available-slots`
        : `/api/v5/resources/${resourceId}/available-slots`
      const res = await client<{ data: Record<string, EffectiveSlot[]> }>({
        method: 'get',
        url,
        params: { dates, timezone },
      })
      return res.data
    },
    enabled: !!resourceId && dates.length > 0,
  })
}

/**
 * Get available slots for a resource type on specific dates.
 * Calls the backend endpoint which applies capacity, advance limitation, permission,
 * and overlap checks for unified availability logic.
 */
export function useResourceTypeAvailableSlots(
  resourceTypeId: number | undefined,
  dates: string[]
) {
  const locationId = useCurrentLocationId()

  return useQuery({
    queryKey: ['resource-type-slots', resourceTypeId, dates, locationId],
    queryFn: async () => {
      const res = await client<{ data: Record<string, EffectiveSlot[]> }>({
        method: 'get',
        url: '/api/v5/resources/type-available-slots',
        params: {
          resource_type_id: resourceTypeId,
          location_id: locationId,
          dates,
          timezone:
            formatStorage.getTimeZone() ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      return res.data
    },
    enabled: !!resourceTypeId && !!locationId && dates.length > 0,
  })
}
